import * as ko from "knockout";
import { ViewModelBase } from "./viewmodelbase";
import "../Templates/validation-error-template.html";
import "../../css/login.css";
import "toastr/build/toastr.css";
import { KnockoutExtensions, NumericValidationOptions } from "../KnockoutExtensions/knockoutextensions";
import { Operation, OperationStatusKind, OperationResultKind } from "../Models/operation";
import { LoginResponse } from "../Models/loginresponse";
import { UserSettingsUpdate } from "../Models/profileupdate";
import { UserSettings } from "../Models/usersettings";
var QRCode = require("../ViewModels/qrcode.min.js");

class LoginViewModel extends ViewModelBase {
    public pageViewModel: LoginViewModel;

    public email = ko.observable<string>(null).extend({ required: true });
    public password = ko.observable<string>(null).extend({ required: true });
    public returnUrl = ko.observable<string>(null);
    public failureMessage = ko.observable<string>(null);
    public isUnconfirmedEmail = ko.observable<boolean>(false);
    public isCardExpiredLockout = ko.observable<boolean>(false);
    public showLogin = ko.observable<boolean>(true);
    public showEnableAuthenticator = ko.observable<boolean>(false);
    public showRecoveryCodes = ko.observable<boolean>(false);
    public show2faLogin = ko.observable<boolean>(false);
    public showPreferences = ko.observable<boolean>(false);
    public authenticatorUri = ko.observable<string>();
    public authenticatorKey = ko.observable<string>();
    public inputCode = ko.observable<string>();
    public hasFocusInputCode = ko.observable(false);
    public hasFocusInputCodeSignIn = ko.observable(false);
    public recoveryCodes = ko.observableArray();
    public traceGainsOptIn = ko.observable<boolean>();
    public marketingOptIn = ko.observable<boolean>(false);

    constructor(email: string, password: string, returnUrl: string) {
        super();

        this.email(email);
        this.password(password);
        this.returnUrl(returnUrl);

        this.pageViewModel = this;
        this.isInitialising(false);
        this.isBusy(false);

        this.showBody();
        if (this.email() == null) {
            this.focusInput();
        } else {
            this.focusInput(".password-container");
        }
    }

    public getPersistenceIncludedProperties(): string[] { return ["email", "password", "returnUrl"]; }

    public getPersistenceIgnoredProperties(): string[] {
        return super.getPersistenceIgnoredProperties().concat(["failureMessage", "isUnconfirmedEmail"]);
    }

    public onKeyPress(data, event) {
        if (event.keyCode === 13) this.signIn2();
        return true;
    }

    public signIn2() {
        var results = this.validate();
        if (results == null) {
            this.isBusy(true);
            this.postJsonData("/api/account/login", this.toJs(), (response: LoginResponse) => {
                if (response.isSuccess && response.recoveryCodes != null && response.recoveryCodes.length > 0) {
                    for (let i = 0; i < response.recoveryCodes.length; i++) {
                        this.recoveryCodes.push(response.recoveryCodes[i]);
                    }
                    this.showLogin(false);
                    this.showEnableAuthenticator(false);
                    this.showRecoveryCodes(true);
                    this.show2faLogin(false);
                    this.isBusy(false);
                }
                else if (response.isSuccess) {

                    if (response.alternateRedirectUrl && response.alternateRedirectUrl.length) {
                        this.navigate(response.alternateRedirectUrl);
                        return;
                    }

                    // Mark if card expired
                    this.writeSetting("card-expired", (response.hasCardExpired ? "1" : "0"));

                    this.writeSetting("nutricalc-login", this.email());
                    this.writeSetting("isLoggedIn", "1");

                    if (response.userSettings.hasExistingUserSettings) {
                        this.writeSetting("recent-recipe", response.userSettings.recentRecipes);
                        this.writeSetting("recent-ingredient", response.userSettings.recentIngredients);
                        this.writeSetting("lastNotificationId", response.userSettings.lastNotificationId.toString());
                        this.writeSetting("copy-to-clipboard-excel", response.userSettings.copyToClipboardExcelFormat);
                        this.writeSetting("reportListViewStyle", response.userSettings.reportsListViewStyle.toString());
                        this.writeSetting("resultListViewStyle", response.userSettings.resultListViewStyle.toString());
                        this.writeSetting("searchOptions", response.userSettings.searchOptions);
                    } else {
                        this.removeSetting("recent-recipe");
                        this.removeSetting("recent-ingredient");
                        this.removeSetting("lastNotificationId");
                        this.removeSetting("copy-to-clipboard-excel");
                        this.removeSetting("reportListViewStyle");
                        this.removeSetting("resultListViewStyle");
                        this.removeSetting("searchOptions");
                    }

                    response.userSettings.recentIngredients = null;
                    response.userSettings.recentRecipes = null;
                    this.writeSetting("settings", JSON.stringify(response.userSettings));
                    this.writeSetting("region", response.region);
                    if (response.teamApiKey && response.teamApiKey.length > 0) {
                        this.writeSetting("team-key", response.teamApiKey);
                    } else {
                        this.removeSetting("team-key");
                    }

                    if (this.returnUrl() != null) {
                        this.navigate(this.returnUrl());
                    } else {
                        this.navigateHome();
                    }
                }
                else if (response.requiresTwoFactor && !response.hasAuthenticator) {
                    this.showLogin(false);
                    this.showEnableAuthenticator(true);
                    this.show2faLogin(false);
                    this.authenticatorKey(response.authenticatorKey);
                    this.authenticatorUri(response.authenticatorUrl);
                    var url = response.authenticatorUrl;
                    document.getElementById('qrCodeData').setAttribute('data-url', url);
                    this.createQRCode(url);
                    this.isBusy(false);
                    this.hasFocusInputCode(true);
                }
                else if (response.requiresTwoFactor && response.hasAuthenticator) {
                    this.showLogin(false);
                    this.showEnableAuthenticator(false);
                    this.show2faLogin(true);
                    this.isBusy(false);
                    this.hasFocusInputCodeSignIn(true);
                }
                else {
                    this.isUnconfirmedEmail(response.isUnconfirmedEmail);
                    this.failureMessage(response.failureMessage);
                    this.isBusy(false);
                }
            }, () => this.isBusy(false));
        }
    }

    public createQRCode(url: string) {
        var x = document.getElementById("qrCode");
        while (x.firstChild) {
            x.removeChild(x.firstChild);
        }
        new QRCode(x,
            {
                text: url,
                width: 150,
                height: 150
            });
    }

    public signIn() {
        var results = this.validate();
        if (results == null) {
            this.isBusy(true);
            this.postJsonData("/api/account/login", this.toJs(), (response: LoginResponse) => {
                if (response.isSuccess) {
                    // Mark if card expired
                    this.writeSetting("card-expired", (response.hasCardExpired ? "1" : "0"));

                    this.writeSetting("nutricalc-login", this.email());
                    this.writeSetting("isLoggedIn", "1");

                    if (response.userSettings.hasExistingUserSettings) {
                        this.writeSetting("recent-recipe", response.userSettings.recentRecipes);
                        this.writeSetting("recent-ingredient", response.userSettings.recentIngredients);
                        this.writeSetting("lastNotificationId", response.userSettings.lastNotificationId.toString());
                        this.writeSetting("copy-to-clipboard-excel", response.userSettings.copyToClipboardExcelFormat);
                        this.writeSetting("reportListViewStyle", response.userSettings.reportsListViewStyle.toString());
                        this.writeSetting("resultListViewStyle", response.userSettings.resultListViewStyle.toString());
                        this.writeSetting("searchOptions", response.userSettings.searchOptions);
                    } else {
                        this.removeSetting("recent-recipe");
                        this.removeSetting("recent-ingredient");
                        this.removeSetting("lastNotificationId");
                        this.removeSetting("copy-to-clipboard-excel");
                        this.removeSetting("reportListViewStyle");
                        this.removeSetting("resultListViewStyle");
                        this.removeSetting("searchOptions");
                    }

                    response.userSettings.recentIngredients = null;
                    response.userSettings.recentRecipes = null;
                    this.writeSetting("settings", JSON.stringify(response.userSettings));
                    this.writeSetting("region", response.region);
                    if (response.teamApiKey && response.teamApiKey.length > 0) {
                        this.writeSetting("team-key", response.teamApiKey);
                    } else {
                        this.removeSetting("team-key");
                    }

                    if (this.returnUrl() != null) {
                        this.navigate(this.returnUrl());
                    } else {
                        this.navigateHome();
                    }
                } else {
                    this.isUnconfirmedEmail(response.isUnconfirmedEmail);
                    this.failureMessage(response.failureMessage);
                    this.isBusy(false);
                }
            }, () => this.isBusy(false));
        }
    }

    public register() {
        if (this.email() != null) {
            this.navigate(`/register?emailAddress=${encodeURIComponent(this.email())}`);
        } else {
            this.navigate("/register");
        }
    }

    public resetPassword() {
        this.navigate("/account/forgotpassword" + (this.email() != null ? `?emailAddress=${encodeURIComponent(this.email())}` : ""));
    }

    public resendEmailConfirmation() {
        this.navigate("/account/resendemailconfirmation?emailAddress=" + encodeURIComponent(this.email()));
    }

    public testGA() {
        this.sendDataLayerTag("Professional", 2);
    }

    public savePreferences() {
        this.isBusy(true);
        var optInSettings = new UserSettings();
        optInSettings.traceGainsOptIn = this.traceGainsOptIn();
        optInSettings.marketingOptIn = this.marketingOptIn();
        this.postJsonData("/api/account/SetOptInPreferences", optInSettings);
        if (this.returnUrl() != null) {
            this.navigate(this.returnUrl());
        } else {
            this.navigateHome();
        }
    }

    private sendDataLayerTag(planName: string, amount: number) {
        // tslint:disable-next-line
        (<any>window).dataLayer.push({ 'event': 'logintest', 'subscriptionType': planName, 'subscriptionAmount': amount });
    }

}

function initialise(email: string, password: string, returnUrl: string) {
    const vm = new LoginViewModel(email, password, returnUrl);
    ko.applyBindings(vm);

    // Reset application local storage
    localStorage.removeItem("isLoggedIn");
}

export { initialise };


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./Scripts/ViewModels/loginviewmodel.ts